import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { TableComponent } from 'app/core/components/table-component/table.component';
import { MotifModule } from "@ey-xd/motif-wc-angular"; 
import { AnalysisWarningComponent } from '../analysis-warning/analysis-warning.component';
import { AgentFiveService } from 'app/core/service/agent-five/agent-five.service';
import {
  Subject,
  switchMap,
  takeUntil,
  tap,
  filter,
  catchError,
  EMPTY,
  take,
  Observable,
} from 'rxjs';
import { ToastService } from 'app/core/service/toast/toast.service';
import { HttpErrorResponse, HttpResponse } from '@angular/common/http';
import { saveAs } from 'file-saver';
import { BreakpointObserver } from '@angular/cdk/layout';
import {
  IconoirDownloadSquare,
  IconoirNavArrowDown,
  IconoirOpenNewWindow,
} from '@ey-xd/motif-icon';
import { Tab } from 'app/models/tab';
import { TabListComponent } from 'app/core/components/tab-list/tab-list.component';
import { FileManipulationService } from 'app/core/service/file-manipulation/file-manipulation.service';
import { ApiFile } from 'app/models/api-response';

@Component({
  selector: 'app-agent-five-tab',
  standalone: true,
  imports: [
    TableComponent,
    AnalysisWarningComponent,
    MotifModule,
    CommonModule,
    TabListComponent,
  ],
  templateUrl: './agent-five-tab.component.html',
  styleUrl: './agent-five-tab.component.scss',
})
export class AgentFiveTabComponent implements OnInit {
  IconoirOpenNewWindow = IconoirOpenNewWindow;
  IconoirDownloadSquare = IconoirDownloadSquare;
  IconoirNavArrowDown = IconoirNavArrowDown;

  @Output() tabNumberEmitter = new EventEmitter<number>();
  @Output() emittedReportId = new EventEmitter<string>();

  private reportId: string = '';
  private destroy$ = new Subject<void>();

  private _tabNumber!: number;
  pageMessage: string = '';

  @Input()
  set tabNumber(value: number) {
    this._tabNumber = value;
    this.initialize();
  }

  get tabNumber(): number {
    return this._tabNumber;
  }

  tableData: any[] = [];
  tableDataCopy: any[] = [];

  adjustmentsTableData: any[] = [];

  tableColumns: any[] = [
    {
      header: '#',
      field: 'order',
      type: 'flagged-number',
      class: 'small-column',
    },
    {
      header: 'Name',
      field: 'name',
      type: 'ellipsis-text',
      class: 'small-column',
    },
    {
      header: 'Description',
      field: 'description',
      type: 'text-with-info-tooltip',
      class: 'small-medium-column',
    },
    {
      header: 'AI Analysis',
      field: 'reason',
      type: 'text-quoting-document',
      class: 'medium-column',
    },
  ];

  tableColumnsCopy: any[] = [];

  // when tabNumber is 3
  extraFSColumns: any[] = [
    {
      header: 'FS Value',
      field: 'fs_value',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'Tax Comp',
      field: 'tax_comp_value',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'Tax Comp (rounded)',
      field: 'rounded',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'Status',
      // field: 'status',
      field: 'flagged',
      type: 'equal-text-field-fs-vs-comp',
      class: 'small-column',
    },
  ];

  //when tab number is 4 and inner tab is Supplementary
  extraCYvsPYcolumns: any[] = [
    {
      header: 'C/Y Comp',
      field: 'cy_value',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'P/Y Comp',
      field: 'py_value',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'Status',
      // field: 'status',
      field: 'flagged',
      type: 'equal-text-field-cy-vs-py',
      class: 'small-column',
    },
  ];

  //when tab number is 4 and inner tab is Adjustment of profit
  adjustmentColumns: any[] = [
    {
      header: '',
      field: 'qorder',
      type: 'flagged-number',
      class: 'small-column',
    },
    {
      header: 'Name',
      field: 'name',
      type: 'ellipsis-text',
      class: 'medium-column',
    },
    {
      header: 'C/Y Comp',
      field: 'cy_comp',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'P/Y Comp',
      field: 'py_comp',
      type: 'currency',
      class: 'small-medium-column',
    },
    {
      header: 'Difference',
      field: 'difference_cy_comp_py_comp',
      type: 'currency',
      class: 'small-column',
    },
    {
      header: 'Difference(%)',
      field: 'difference_percentage',
      type: 'percentage',
      class: 'small-column',
    },
  ];

  flaggedData: any[] = [];
  passedTestData: any[] = [];

  loading: boolean = true;
  contentAvailable: boolean = false; // Variable to track content availability
  showWarning: boolean = false;
  showOnlyFlaggedData: boolean = false;

  exportingLoader: boolean = false;

  // For testing
  @ViewChild('analysisWarning') analysisWarning?: AnalysisWarningComponent;
  saveAs = saveAs;

  // Zoom related properties for breakpoint observer
  isZoomed: boolean = false;

  // track timestamp and filenames
  lastRanOn: string = '';
  lastExportedOn: string = ''; // timestamp for FS, FS vs Comp and CY vs PY (Supp.)
  adjustmentsLastExportedOn: string = ''; // timestamp for CY vs PY (Adj. of profit)
  filenames: string = '';

  // id for user guide file
  userGuideId: string | undefined = '';

  // inner tabs for CY v PY
  cyvpyTabs: Tab[] = [
    { tabName: 'Adjustment of profit', tabNumberOrder: 1, isActive: true },
    {
      tabName: 'Supplementary',
      tabNumberOrder: 2,
      isActive: false,
    },
  ];

  innerCurrentTab: Tab = {
    tabName: 'Adjustment of profit',
    tabNumberOrder: 1,
    isActive: true,
  };

  adjustmentOfProfitMessage: string =
    'The purpose of the test is to provide a schedule showing the profit before tax and trading income tax adjustments included within the Current Year and Prior Year Tax Computation along with variance analysis (actual amount and percentage).';

  constructor(
    private agentFiveService: AgentFiveService,
    private toastService: ToastService,
    private breakpointObserver: BreakpointObserver,
    private fileManipulationService: FileManipulationService
  ) {
    this.breakpointObserver
      .observe(['(max-width: 800px)'])
      .subscribe((result) => {
        this.isZoomed = result.matches;
      });
  }

  ngOnInit(): void {
    this.getUserGuideId();
  }

  initialize() {
    this.restartVariables();

    this.setTableColumns();

    switch (this.tabNumber) {
      // case 1: is for home, not needed
      case 2: // FS Check
        this.agentFiveService
          .getFSCheckLatestId()
          .pipe(
            tap((data: any) => (this.reportId = this.getLatestProjectId(data))),
            tap((_) => this.emittedReportId.emit(this.reportId)),
            switchMap(() =>
              this.agentFiveService.getFSChecksResult(this.reportId).pipe(
                tap((data: any) => {
                  // Check if result is ready before proceeding
                  const contentAvailable: boolean = this.resultIsReady(data);
                  if (!contentAvailable) {
                    this.loading = false;
                    this.contentAvailable = false;
                    this.pageMessage =
                      'The result is not available yet. Please come back in a few minutes after the proccess is finished.';
                  } else {
                    this.contentAvailable = true;
                  }
                }),
                filter(() => this.contentAvailable), // Only continue if content is available
                tap((data: any) => (this.tableData = this.getTableData(data))),
                tap((_) => this.categorizeRows()),
                tap((_) => (this.loading = false)),
                // catch error for inner call
                catchError((err: HttpErrorResponse) => {
                  this.loading = false;
                  this.contentAvailable = false; // Indicate that content is unavailable
                  this.pageMessage =
                    'There is no result available here because the process failed. Please try running your documents again.';
                  return EMPTY;
                })
              )
            ),
            takeUntil(this.destroy$), // Handle unsubscription
            // Case where there is no report or there is no processing report and the user gets there
            catchError((err: HttpErrorResponse) => {
              this.loading = false;
              this.contentAvailable = false;
              this.pageMessage =
                'There is no data in this page. Please run the check first to be able to see your report.';
              return EMPTY;
            })
          )
          .subscribe();
        break;
      case 3: // FS vs Comp
        this.agentFiveService
          .getFSvsCompLatestId()
          .pipe(
            tap((data: any) => (this.reportId = this.getLatestProjectId(data))),
            tap((_) => this.emittedReportId.emit(this.reportId)),
            switchMap(() =>
              this.agentFiveService.getFSvsCompResult(this.reportId).pipe(
                tap((data: any) => {
                  // Check if result is ready before proceeding
                  const contentAvailable: boolean = this.resultIsReady(data);
                  if (!contentAvailable) {
                    this.loading = false;
                    this.contentAvailable = false;
                    this.pageMessage =
                      'The result is not available yet. Please come back in a few minutes after the proccess is finished.';
                  } else {
                    this.contentAvailable = true;
                  }
                }),
                filter(() => this.contentAvailable), // Only continue if content is available
                tap((data: any) => (this.tableData = this.getTableData(data))),
                tap(
                  (_) =>
                    (this.tableColumns = [
                      ...this.tableColumns,
                      ...this.extraFSColumns,
                    ])
                ),
                tap((_) => this.categorizeRows()),
                tap((_) => (this.loading = false)),
                // catch error for inner call
                catchError((err: HttpErrorResponse) => {
                  this.loading = false;
                  this.contentAvailable = false; // Indicate that content is unavailable
                  this.pageMessage =
                    'There is no result available here because the process failed. Please try running your documents again.';
                  return EMPTY;
                })
              )
            ),
            takeUntil(this.destroy$), // Handle unsubscription
            // Case where there is no report or there is no processing report and the user gets there
            catchError((err: HttpErrorResponse) => {
              this.loading = false;
              this.contentAvailable = false;
              this.pageMessage =
                'There is no data in this page. Please run the check first to be able to see your report.';
              return EMPTY;
            })
          )
          .subscribe();
        break;
      case 4: // P/Y vs C/Y Comp
        this.agentFiveService
          .getCYvsPYLatestId()
          .pipe(
            tap((data: any) => (this.reportId = this.getLatestProjectId(data))),
            tap((_) => this.emittedReportId.emit(this.reportId)),
            switchMap(() =>
              this.agentFiveService.getCYvsPYResult(this.reportId).pipe(
                tap((data: any) => {
                  // Check if result is ready before proceeding
                  const contentAvailable: boolean = this.resultIsReady(data);
                  if (!contentAvailable) {
                    this.loading = false;
                    this.contentAvailable = false;
                    this.pageMessage =
                      'The result is not available yet. Please come back in a few minutes after the proccess is finished.';
                  } else {
                    this.contentAvailable = true;
                  }
                }),
                filter(() => this.contentAvailable), // Only continue if content is available
                tap((data: any) => (this.tableData = this.getTableData(data))),
                tap((_) => {
                  this.tableColumnsCopy = [...this.tableColumns];
                  this.tableDataCopy = [...this.tableData];

                  this.tableColumns = this.adjustmentColumns;
                  this.tableData = this.adjustmentsTableData;
                  this.resetInnerTabs();
                }),
                tap((_) => this.categorizeRows()),
                tap((_) => (this.loading = false)),
                // catch error for inner call
                catchError((err: HttpErrorResponse) => {
                  this.loading = false;
                  this.contentAvailable = false; // Indicate that content is unavailable
                  this.pageMessage =
                    'There is no result available here because the process failed. Please try running your documents again.';
                  return EMPTY;
                })
              )
            ),
            takeUntil(this.destroy$), // Handle unsubscription
            // Case where there is no report or there is no processing report and the user gets there
            catchError((err: HttpErrorResponse) => {
              this.loading = false;
              this.contentAvailable = false;
              this.pageMessage =
                'There is no data in this page. Please run the check first to be able to see your report.';
              return EMPTY;
            })
          )
          .subscribe();

        break;
    }
  }

  private restartVariables() {
    this.agentFiveService.currentTooltip = null;
    this.tableData = [];
    this.flaggedData = [];
    this.passedTestData = [];
    this.reportId = '';
    this.emittedReportId.emit(this.reportId);
    this.loading = true;
    this.contentAvailable = false;
    this.showWarning = false;
    this.showOnlyFlaggedData = false;
    if (this.tableColumns.length > 4) {
      this.tableColumns = this.tableColumns.slice(0, 4);
    }
  }

  private setTableColumns() {
    this.tableColumns = [
      {
        header: '#',
        field: 'order',
        type: 'flagged-number',
        class: 'small-column',
      },
      {
        header: 'Name',
        field: 'name',
        type: 'ellipsis-text',
        class: 'small-column',
      },
      {
        header: 'Description',
        field: 'description',
        type: 'text-with-info-tooltip',
        class: 'small-medium-column',
      },
      {
        header: 'AI Analysis',
        field: 'reason',
        type:
          this.tabNumber === 2
            ? 'text-quoting-document-with-tooltip'
            : 'text-quoting-document',
        class: 'medium-column',
      },
    ];
  }
  //

  private resultIsReady(data: any): boolean {
    let checkIsProcessing: boolean = false;

    const storedCheck = '';

    // If there is an storedCheck for the tab it means the result is
    // still processing in the Agent 5 home page and backend. So for consistency
    // purposes we show the message accordingly so we don't show old data or
    // something that may cause confusion
    if (storedCheck) {
      checkIsProcessing = true;
    }

    return (
      data.items[0].processes[0].q_result?.length > 0 && !checkIsProcessing
    );
  }

  private getTableData(data: any): any[] {
    this.getTimestampsAndFilenames(data);
    const toret: any[] = data.items[0]?.processes[0]?.q_result || [];

    const reportFileNames: string[] = this.getReportFilesInfo(
      data.items[0]?.processes[0]?.files,
      'filename'
    );

    const reportFileIds: string[] = this.getReportFilesInfo(
      data.items[0]?.processes[0]?.files,
      'id'
    );

    if (this.tabNumber === 4) {
      // find the one with schedule_a
      this.adjustmentsTableData =
        toret
          .find((result) => result.schedule_a !== null)
          ?.schedule_a?.map((schedItem: any) => {
            return {
              // we add order key to avoid messy sorting logic in other functions
              ...schedItem,
              order: schedItem.index,
            };
          }) || [];

      // we remove the row with order 19 or the one with schedule_a
      const index: number = toret.findIndex(
        (result) => result.schedule_a !== null
      );

      if (index !== -1) {
        toret.splice(index, 1);
      }
    }

    return toret.map((row) => {
      return {
        ...row,
        filenames: reportFileNames,
        fileIds: reportFileIds,
      };
    });
  }

  private getTimestampsAndFilenames(data: any) {
    const reportFileNames: string[] = this.getReportFilesInfo(
      data.items[0]?.processes[0]?.files,
      'filename'
    );

    this.lastRanOn = data.items[0]?.processes[0].created_at;
    this.lastExportedOn = data.items[0]?.processes[0].last_exported_at;
    if (this.lastExportedOn) this.lastExportedOn += 'Z';
    this.filenames = reportFileNames.join(' and ');
    this.adjustmentsLastExportedOn =
      data.items[0]?.processes[0].adjustments_last_exported_at;
    if (this.adjustmentsLastExportedOn) this.adjustmentsLastExportedOn += 'Z';
  }

  private getLatestProjectId(data: any) {
    return data?.items[0]?.id;
  }

  private getReportFilesInfo(
    files: { id: string; type: number; filename: string }[],
    key: 'filename' | 'id'
  ): string[] {
    // tabNumber === 2 -> FS Checks -> type 50 (solo una file)
    // tabNumber === 3 -> FS vs Comp -> type 50 y type 51
    // tabNumber === 4 ->  CY vs PY Comp -> type 51 (CY) y type 52 (PY)
    const toret: string[] = [];

    let types: number[] = [];

    switch (this.tabNumber) {
      case 2: //FS
        types = [50];
        break;
      case 3: // FS vs Comp
        types = [50, 51];
        break;
      case 4: // PY vs CY
        types = [52, 51];
        break;
    }

    types.forEach((type: number) => {
      const file = files.find(
        (f: { id: string; type: number; filename: string }) => f.type === type
      );
      if (file) {
        return toret.push(file[key]);
      }
      return;
    });

    return toret;
  }

  /**
   * This function separates the data in different tables so we have the items that require attention in the upper table
   * as intended in the UI design
   */
  categorizeRows() {
    this.flaggedData = [];
    this.passedTestData = [];
    this.showWarning = false;

    this.tableData.forEach((data) => {
      if (data.flagged) {
        this.flaggedData.push(data);
      } else {
        this.passedTestData.push(data);
      }
    });

    if (this.tabNumber === 4 && this.innerCurrentTab.tabNumberOrder === 1) {
      if (this.passedTestData.length > 0) {
        this.passedTestData.sort((a: any, b: any) => a.order - b.order);
      }
    } else {
      if (this.flaggedData.length > 0) {
        this.flaggedData.sort((a: any, b: any) => a.order - b.order);
        this.showWarning = true;
      }

      if (this.passedTestData.length > 0) {
        this.passedTestData.sort((a: any, b: any) => a.order - b.order);
      }
    }
  }

  //Buttons functionalities
  private getUserGuideId() {
    const tag: string = 'USER_GUIDE_ANOMALY_DETECTION';
    this.fileManipulationService
      .getFileWithTag(tag)
      .pipe(take(1))
      .subscribe({
        next: (data: ApiFile) => {
          const id = data.id;
          this.userGuideId = id;
        },
        error: (error: HttpErrorResponse) => {},
      });
  }

  openUserGuide() {
    this.fileManipulationService
      .getDownloadFile(this.userGuideId)
      .pipe(take(1))
      .subscribe({
        next: (data: Blob) => {
          const file = new Blob([data], { type: 'application/pdf' });
          const blobUrl = URL.createObjectURL(file);
          window.open(`${blobUrl}`, '_blank');
          // Cleanup after a short delay to prevent premature revocation
          // setTimeout(() => {
          //   URL.revokeObjectURL(blobUrl);
          // }, 5000);
        },
        error: (error: HttpErrorResponse) => {
          const message: string = error.error?.message;

          this.toastService.showToast('error', `${message}`, {
            classTheme: '',
            autoHide: false,
          });
        },
      });
  }

  // if parameter is passed as true, we indicate that it is excel
  exportTestResults(isExcel: boolean = false) {
    this.exportingLoader = true;

    switch (this.tabNumber) {
      // case 1: is for home, not needed
      case 2: {
        // FS
        this.lastExportedOn = `${Date.now()}`;

        const export$: Observable<any> = isExcel
          ? this.agentFiveService.getFSCheckExportedAsExcel(this.reportId)
          : this.agentFiveService.getFSCheckExported(this.reportId);

        export$.pipe(take(1)).subscribe({
          next: (response: HttpResponse<Blob>) => {
            this.exportingLoader = false;
            this.handleExportResponse(response, isExcel);
            this.agentFiveService
              .patchFSCheckLastExportedAt(this.reportId)
              .pipe(take(1))
              .subscribe();
          },
          error: (error: HttpErrorResponse) => {
            const message: string =
              error.error?.message || 'An unexpected error occurred';
            this.exportingLoader = false;
            this.toastService.showToast('error', `${message}`, {
              classTheme: '',
              autoHide: false,
            });
          },
        });
        break;
      }
      case 3: {
        // FS vs Comp
        this.lastExportedOn = `${Date.now()}`;

        const export$: Observable<any> = isExcel
          ? this.agentFiveService.getFSvsCompExportedAsExcel(this.reportId)
          : this.agentFiveService.getFSvsCompExported(this.reportId);

        export$.pipe(take(1)).subscribe({
          next: (response: HttpResponse<Blob>) => {
            this.exportingLoader = false;
            this.handleExportResponse(response, isExcel);
            this.agentFiveService
              .patchFSvsCompLastExportedAt(this.reportId)
              .pipe(take(1))
              .subscribe();
          },
          error: (error: HttpErrorResponse) => {
            const message: string =
              error.error?.message || 'An unexpected error occurred';
            this.exportingLoader = false;
            this.toastService.showToast('error', `${message}`, {
              classTheme: '',
              autoHide: false,
            });
          },
        });
        break;
      }
      case 4: {
        // CY vs PY
        let export$: Observable<any>;
        let patchExportedDate$: Observable<any>;

        if (this.inAdjustmentsInnerTab()) {
          this.adjustmentsLastExportedOn = `${Date.now()}`;

          export$ = isExcel
            ? this.agentFiveService.getCYvsPYAdjExportedAsExcel(this.reportId)
            : this.agentFiveService.getCYvsPYAdjExported(this.reportId);

          patchExportedDate$ =
            this.agentFiveService.patchCYvsPYAdjLastExportedAt(this.reportId);
        } else {
          this.lastExportedOn = `${Date.now()}`;

          export$ = isExcel
            ? this.agentFiveService.getCYvsPYExportedAsExcel(this.reportId)
            : this.agentFiveService.getCYvsPYExported(this.reportId);

          patchExportedDate$ =
            this.agentFiveService.patchCYvsPYSuppLastExportedAt(this.reportId);
        }

        export$.pipe(take(1)).subscribe({
          next: (response: HttpResponse<Blob>) => {
            this.exportingLoader = false;
            this.handleExportResponse(response, isExcel);
            patchExportedDate$.pipe(take(1)).subscribe();
          },
          error: (error: HttpErrorResponse) => {
            const message: string =
              error.error?.message || 'An unexpected error occurred';
            this.exportingLoader = false;
            this.toastService.showToast('error', `${message}`, {
              classTheme: '',
              autoHide: false,
            });
          },
        });
        break;
      }
    }
  }

  toggleTab(tab: Tab) {
    this.toastService.clearToastWithTag(true);

    this.cyvpyTabs.forEach((tb: Tab) =>
      tb.tabNumberOrder === tab.tabNumberOrder
        ? (tb.isActive = true)
        : (tb.isActive = false)
    );
    this.innerCurrentTab = tab;

    this.changeContentForSupplementary();
  }

  toggleTabOnNumber($event: number) {
    const tab: Tab | undefined = this.cyvpyTabs.find(
      (tb: Tab) => tb.tabNumberOrder === $event
    );

    if (tab) {
      this.toggleTab(tab);
    }
  }

  private changeContentForSupplementary() {
    if (this.innerCurrentTab.tabNumberOrder === 1) {
      // Adjustment of profit
      this.tableData = this.adjustmentsTableData;
      this.tableColumns = this.adjustmentColumns;
      this.categorizeRows();
    }
    if (this.innerCurrentTab.tabNumberOrder === 2) {
      // Supplementary
      this.tableData = this.tableDataCopy;
      this.tableColumns = [
        ...this.tableColumnsCopy,
        ...this.extraCYvsPYcolumns,
      ];
      this.categorizeRows();
    }
  }

  // To handle the export response and take the filename from
  // Content-Disposition header
  private handleExportResponse(
    response: HttpResponse<Blob>,
    isExcel: boolean = false
  ): void {
    const extension: string = isExcel ? '.xlsx' : '.pdf';

    if (!response.body) {
      return;
    }

    const data: Blob = response.body;
    let filename = `Exported_test_results${extension}`;

    const content = response.headers.get('Content-Disposition');

    if (content) {
      const parts = content.split("filename*=UTF-8''");
      if (parts[1]) {
        filename = decodeURIComponent(parts[1]);
      }
    }

    this.saveAs(data, filename);
  }

  passedTestDataExists(): boolean {
    return this.passedTestData.length > 0;
  }

  flaggedDataExists(): boolean {
    return this.flaggedData.length > 0;
  }

  private resetInnerTabs() {
    this.cyvpyTabs.forEach((tab: Tab) => {
      if (tab.tabNumberOrder === 1) {
        tab.isActive = true;
        this.innerCurrentTab = tab;
      } else {
        tab.isActive = false;
      }
    });
  }

  inAdjustmentsInnerTab(): boolean {
    return this.tabNumber === 4 && this.innerCurrentTab.tabNumberOrder === 1;
  }

  inSupplementaryInnerTab(): boolean {
    return this.tabNumber === 4 && this.innerCurrentTab.tabNumberOrder === 2;
  }

  showOnlyFlagged() {
    this.showOnlyFlaggedData = !this.showOnlyFlaggedData;
  }

  backToAgentFiveHome() {
    this.tabNumberEmitter.emit(1);
  }

  scrollUp() {
    window.scrollTo({
      top: 0,
      behavior: 'smooth', // Adds smooth scrolling effect
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete(); // Signals observables to unsubscribe and cleans up memory
  }
}
